import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Users, UserPlus, Upload, Tag, Search, CheckCircle2, XCircle, ShieldCheck, X } from 'lucide-react'
import { getSession } from '../lib/session'

// The tenant's contact book — the audience every WhatsApp broadcast draws from.
// A contact is only broadcastable once opt-in is true (Meta policy + law), so we
// surface that state loudly and make it one click to flip.
export default function Contacts() {
  const session = getSession()
  const [contacts, setContacts] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState('')
  const [tagFilter, setTagFilter] = useState('')
  const [optFilter, setOptFilter] = useState('') // '', 'true', 'false'
  const [showAdd, setShowAdd] = useState(false)
  const [showImport, setShowImport] = useState(false)

  const load = () => {
    if (!session?.namespaceId) return
    setLoading(true)
    const params = {}
    if (q.trim()) params.q = q.trim()
    if (tagFilter.trim()) params.tag = tagFilter.trim()
    if (optFilter) params.opt_in = optFilter
    axios.get('/api/v1/campaigns/contacts', { params })
      .then(r => { setContacts(r.data.contacts || []); setTotal(r.data.total ?? (r.data.contacts || []).length) })
      .catch(err => toast.error(err.response?.data?.error || 'Failed to load contacts'))
      .finally(() => setLoading(false))
  }
  useEffect(load, [session?.namespaceId, tagFilter, optFilter])

  const toggleOptIn = async (c) => {
    const next = !c.opt_in
    setContacts(list => list.map(x => x.id === c.id ? { ...x, opt_in: next } : x)) // optimistic
    try {
      const r = await axios.patch(`/api/v1/campaigns/contacts/${c.id}/opt-in`, { opt_in: next, source: 'manual' })
      setContacts(list => list.map(x => x.id === c.id ? r.data : x))
    } catch (err) {
      setContacts(list => list.map(x => x.id === c.id ? { ...x, opt_in: !next } : x))
      toast.error(err.response?.data?.error || 'Could not update opt-in')
    }
  }

  const optedIn = contacts.filter(c => c.opt_in).length

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users size={24} className="text-primary-600" /> Contacts</h2>
          <p className="text-gray-500 mt-1">Your CRM — the opted-in audience your campaigns reach.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowImport(true)} className="btn-secondary text-sm flex items-center gap-2"><Upload size={16} /> Import CSV</button>
          <button onClick={() => setShowAdd(true)} className="btn-primary text-sm flex items-center gap-2"><UserPlus size={16} /> Add contact</button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {[
          { label: 'Contacts', value: total, icon: Users, color: 'text-primary-600' },
          { label: 'Opted in', value: optedIn, icon: CheckCircle2, color: 'text-green-600' },
          { label: 'Not opted in', value: contacts.length - optedIn, icon: XCircle, color: 'text-gray-400' },
        ].map((s, i) => (
          <div key={i} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{s.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{Number(s.value).toLocaleString()}</p>
              </div>
              <s.icon className={s.color} size={22} />
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Search name, phone, email…" className="input pl-9" />
        </div>
        <div className="relative min-w-[160px]">
          <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={tagFilter} onChange={e => setTagFilter(e.target.value)} placeholder="Filter by tag" className="input pl-9" />
        </div>
        <select value={optFilter} onChange={e => setOptFilter(e.target.value)} className="input w-auto">
          <option value="">All opt-in states</option>
          <option value="true">Opted in</option>
          <option value="false">Not opted in</option>
        </select>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-gray-500">
              <th className="px-4 py-3 font-medium">Contact</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Tags</th>
              <th className="px-4 py-3 font-medium text-center">Opt-in</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
            {!loading && contacts.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-400">No contacts yet. Add one or import a CSV to get started.</td></tr>
            )}
            {contacts.map(c => (
              <tr key={c.id} className="border-b border-gray-100 last:border-0">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{c.name || '—'}</p>
                  {c.email && <p className="text-xs text-gray-400">{c.email}</p>}
                </td>
                <td className="px-4 py-3 tabular-nums text-gray-600">+{c.wa_id}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(c.tags || []).map(t => (
                      <span key={t} className="text-xs bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5 text-gray-600">{t}</span>
                    ))}
                    {(!c.tags || c.tags.length === 0) && <span className="text-gray-300">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => toggleOptIn(c)}
                    className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${
                      c.opt_in ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}>
                    {c.opt_in ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                    {c.opt_in ? 'Opted in' : 'Opt in'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddContactModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load() }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load() }} />}
    </div>
  )
}

function AddContactModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ phone: '', name: '', email: '', tags: '', opt_in: true })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const submit = async () => {
    if (!form.phone.trim()) { toast.error('A phone / WhatsApp number is required'); return }
    setBusy(true)
    try {
      await axios.post('/api/v1/campaigns/contacts', {
        phone: form.phone.trim(),
        name: form.name.trim() || undefined,
        email: form.email.trim() || undefined,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        opt_in: form.opt_in,
      }, { validateStatus: s => s < 500 }).then(r => { if (r.status >= 400) throw new Error(r.data.error) })
      toast.success('Contact saved')
      onSaved()
    } catch (err) { toast.error(err.response?.data?.error || err.message || 'Could not save contact') }
    finally { setBusy(false) }
  }

  return (
    <Modal title="Add contact" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Phone / WhatsApp number *"><input className="input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 555 123 4567" /></Field>
        <Field label="Name"><input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Doe" /></Field>
        <Field label="Email"><input className="input" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@example.com" /></Field>
        <Field label="Tags (comma-separated)"><input className="input" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="lead, vip" /></Field>
        <label className="flex items-start gap-2 text-sm text-gray-600 pt-1">
          <input type="checkbox" checked={form.opt_in} onChange={e => set('opt_in', e.target.checked)} className="mt-0.5" />
          <span><b className="text-gray-800">Opted in to marketing.</b> Only opted-in contacts can be broadcast to — confirm you have consent.</span>
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-primary text-sm disabled:opacity-60">{busy ? 'Saving…' : 'Save contact'}</button>
      </div>
    </Modal>
  )
}

function ImportModal({ onClose, onDone }) {
  const [raw, setRaw] = useState('')
  const [defaultOptIn, setDefaultOptIn] = useState(false)
  const [defaultTags, setDefaultTags] = useState('')
  const [busy, setBusy] = useState(false)

  // Parse a pasted CSV: header row (phone,name,email,tags) or a bare phone per line.
  const parse = () => {
    const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
    if (!lines.length) return []
    const header = lines[0].toLowerCase()
    const hasHeader = /phone|wa_id|number|name|email/.test(header)
    const cols = hasHeader ? lines[0].split(',').map(c => c.trim().toLowerCase()) : ['phone', 'name', 'email', 'tags']
    const body = hasHeader ? lines.slice(1) : lines
    return body.map(line => {
      const parts = line.split(',').map(p => p.trim())
      const row = {}
      cols.forEach((c, i) => { if (parts[i]) row[c] = parts[i] })
      const phone = row.phone || row.wa_id || row.number || parts[0]
      const tags = row.tags ? row.tags.split(/[;|]/).map(t => t.trim()).filter(Boolean) : []
      return { phone, name: row.name, email: row.email, tags }
    }).filter(r => r.phone)
  }

  const preview = parse()

  const submit = async () => {
    if (!preview.length) { toast.error('Nothing to import — paste some rows first'); return }
    setBusy(true)
    try {
      const r = await axios.post('/api/v1/campaigns/contacts/import', {
        contacts: preview,
        default_opt_in: defaultOptIn,
        default_tags: defaultTags.split(',').map(t => t.trim()).filter(Boolean),
      })
      toast.success(`Imported ${r.data.imported} · skipped ${r.data.skipped}`)
      onDone()
    } catch (err) { toast.error(err.response?.data?.error || 'Import failed') }
    finally { setBusy(false) }
  }

  return (
    <Modal title="Import contacts" onClose={onClose}>
      <p className="text-sm text-gray-500 mb-3">Paste CSV rows. First column is the phone number; optional columns: <code className="text-xs bg-gray-100 px-1 rounded">name, email, tags</code>. A header row is auto-detected.</p>
      <textarea value={raw} onChange={e => setRaw(e.target.value)} rows={7}
        placeholder={'phone,name,email,tags\n+15551234567,Jane Doe,jane@example.com,vip'}
        className="input font-mono text-xs" />
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <Field label="Default tags (comma-separated)"><input className="input" value={defaultTags} onChange={e => setDefaultTags(e.target.value)} placeholder="imported" /></Field>
        <label className="flex items-center gap-2 text-sm text-gray-600 sm:pt-6">
          <input type="checkbox" checked={defaultOptIn} onChange={e => setDefaultOptIn(e.target.checked)} />
          Mark all as opted in
        </label>
      </div>
      <div className="flex items-center justify-between mt-5">
        <span className="text-xs text-gray-400 flex items-center gap-1"><ShieldCheck size={13} /> {preview.length} valid row{preview.length === 1 ? '' : 's'} detected</span>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !preview.length} className="btn-primary text-sm disabled:opacity-60">{busy ? 'Importing…' : `Import ${preview.length}`}</button>
        </div>
      </div>
    </Modal>
  )
}

function Field({ label, children }) {
  return (<label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">{label}</span>{children}</label>)
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-gray-100 border border-gray-200 rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}
